<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentFilterFlags from "@components/Filter/FilterFlags.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentPagination from "@components/Pagination/Pagination.vue";
  import {router, useForm, usePage} from "@inertiajs/vue3";
  import PatientinputClientComponentDataSectionTableRow from "@pages/PatientinputClient/Components/DataSectionTableRow.vue";
  import PatientinputClientComponentDeleteDialog from "@pages/PatientinputClient/Components/DeleteDialog.vue";
  import PatientinputClientComponentImportDialog from "@pages/PatientinputClient/Components/ImportDialog.vue";
  import {recordOriginTexts} from "@pages/Records/Enums/Enums.js";
  import {MedSpinner} from "@pharma4u/medicheck-components";
  import {cloneDeep, debounce} from "es-toolkit";
  import {compactDecrypt, importPKCS8} from "jose";

  defineOptions({
    name: "PatientinputClientComponentDataSection",
  });

  const privacy = inject("$privacy");
  const page = usePage();

  const refImportDialog = ref(null);
  const refDeleteDialog = ref(null);

  const transfers = ref([]);
  const transferToDelete = ref(null);

  const availableFlags = {
    origin: {
      label: "Herkunft",
      nullable: false,
      options: {
        default: recordOriginTexts.patientinput,
        checklist: recordOriginTexts.carerinput,
      },
    },
    status: {
      label: "Bearbeitungsstand",
      nullable: false,
      options: {
        default: "unbearbeitet",
        processed: "in Bearbeitung",
        completed: "abgeschlossen",
        rejected: "gelöscht",
      },
    },
    createdWithinWeek: "Innerhalb der letzten 7 Tage eingesendet",
    createdWithinMonth: "Innerhalb der letzten 30 Tage eingesendet",
  };

  const form = useForm({
    order: page.props?.filters?.order || "-created_at",
    q: {
      search: page.props?.filters?.q?.search || null,
    },
    flags: page.props?.filters?.flags || [],
  });

  const transfersEncrypted = computed(() => page.props.transfersEncrypted);

  watch(
    () => page.props.transfersEncrypted,
    () => {
      filterTransfers();
    },
  );

  watch(
    () => form.order,
    debounce(function () {
      submitFilter();
    }, 500),
  );

  watch(
    () => form.q.search,
    debounce(function () {
      submitFilter();
    }, 500),
  );

  watch(
    () => form.flags,
    debounce(function () {
      submitFilter();
    }, 500),
    {deep: true}, // required when watching arrays
  );

  onMounted(async () => {
    filterTransfers();
  });

  const orderBy = (by) => {
    if (form.order.includes(by)) {
      // already sorted by this colum, so flip direction
      form.order =
        by.slice(0, 1) === "-"
          ? // asc
            by.slice(1) // remove "-"
          : // desc
            "-" + by; // add "-"
    } else {
      // not already sorted by this column, asc is default
      form.order = by;
    }
  };

  const filterTransfers = () => {
    form.processing = true;

    privacy.whenCryptReady(async () => {
      const privateKeyDecrypted = await privacy.decryptValue(page.props.privateKeyEncrypted);
      const privateKey = await importPKCS8(privateKeyDecrypted, "RSA-OAEP");

      const promises = [...page.props.transfersEncrypted.data].map((encryptedTransfer) => {
        const transfer = cloneDeep(encryptedTransfer);

        return compactDecrypt(transfer.data, privateKey).then(({plaintext}) => {
          const decryptedTransfer = new TextDecoder().decode(plaintext);
          transfer.data = JSON.parse(decryptedTransfer);
          return transfer;
        });
      });

      Promise.all(promises).then((decryptedTransfers) => {
        if (form?.q?.search?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)) {
          transfers.value = decryptedTransfers;
        } else {
          const words = (form?.q?.search?.toLowerCase() || "").split(" ").filter((word) => word.trim() !== "");

          transfers.value =
            words.length > 0
              ? decryptedTransfers.filter((transfer) => {
                  return words.every((word) => {
                    if (/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.test(word)) {
                      const [day, month, year] = word.split(".");
                      word = year + "-" + month.padStart(2, "0") + "-" + day.padStart(2, "0");
                    } else if (/^(\d{1,2})(?:\.(\d{1,2})?\.?)?$/.test(word)) {
                      const [day, month] = word.split(".");
                      word = month
                        ? // patterns like dd.mm or dd.mm.
                          "-" + month.padStart(2, "0") + "-" + day.padStart(2, "0")
                        : // patterns like dd oder dd.
                          "-" + day.padStart(2, "0");
                    }

                    return (
                      (transfer.data.masterdata?.patient?.lastname &&
                        transfer.data.masterdata.patient.lastname.toLowerCase().includes(word.toLowerCase())) ||
                      (transfer.data.masterdata?.patient?.firstname &&
                        transfer.data.masterdata.patient.firstname.toLowerCase().includes(word.toLowerCase())) ||
                      (transfer.data.masterdata?.patient?.birthdate &&
                        transfer.data.masterdata.patient.birthdate.includes(word.toLowerCase())) ||
                      (transfer.data.masterdata?.patient?.insurancenumber &&
                        transfer.data.masterdata.patient.insurancenumber.toLowerCase().startsWith(word.toLowerCase()))
                    );
                  });
                })
              : decryptedTransfers;
        }

        form.processing = false;
      });
    });
  };

  const submitFilter = () => {
    form.get(route("patientinput-client.index"), {preserveState: true, preserveScroll: true, replace: true});
  };

  const startImport = (transfer) => {
    refImportDialog.value.open(transfer);
  };

  const startDelete = (transfer) => {
    transferToDelete.value = transfer;
    refDeleteDialog.value.open();
  };

  const cancelDelete = () => {
    transferToDelete.value = null;
  };

  const performDelete = () => {
    form.processing = true;

    router.delete(route("patientinput-client.destroy", {transfer: transferToDelete.value.id}), {
      onSuccess: () => {
        transferToDelete.value = null;
      },
    });
  };
</script>

<template>
  <section class="w-full">
    <div class="bg-white p-4 shadow-sm">
      <div class="mb-4">
        <h2 class="text-2xl font-semibold">Bereits übermittelte Fragebögen</h2>
        <p>Nicht importierte Fragebögen werden nach 90 Tagen automatisch unwiderruflich gelöscht.</p>
      </div>

      <!-- filter :: start -->
      <div class="mt-2">
        <form @submit.prevent="submitFilter">
          <div class="flex items-baseline justify-between space-x-8">
            <ComponentInput
              v-model="form.q.search"
              autocomplete="off"
              class="max-w-52"
              placeholder="Suchen"
              :test-id="getTestId('data-section-search')"
            >
              <template #icon>
                <ComponentIcon class="text-xl text-gray-600">search</ComponentIcon>
              </template>
            </ComponentInput>

            <ComponentFilterFlags
              v-model="form.flags"
              :available-flags="availableFlags"
              :test-id="getTestId('data-section-flags')"
            />
          </div>
        </form>
      </div>
      <!-- filter :: end -->

      <!-- table listing :: start -->
      <article class="mt-4">
        <template v-if="form.processing">
          <div class="grid min-h-32 w-full place-content-center">
            <MedSpinner size="2xl" :data-test="getTestId('data-section-spinner')" />
          </div>
        </template>

        <template v-else>
          <table class="w-full table-auto">
            <thead>
              <tr class="border-b-2 border-gray-400 leading-tight">
                <th class="px-0.5 py-2 text-left align-top font-normal">
                  <div>Name, Vorname</div>
                  <div class="text-xs">Geburtsdatum, Versichertennummer</div>
                </th>

                <th class="px-0.5 py-2 text-left align-top font-normal">Herkunft</th>

                <th class="px-0.5 py-2 text-left align-top font-normal">
                  <div>
                    <!-- TODO which button? -->
                    <button
                      class="flex cursor-pointer items-center space-x-2 text-left"
                      :data-test="getTestId('data-section-sort-by-status-button')"
                      @click.prevent="orderBy('-status')"
                    >
                      <span>Bearbeitungsstand</span>
                      <ComponentIcon v-if="form.order === 'status'">expand_less</ComponentIcon>
                      <ComponentIcon v-else-if="form.order === '-status'">expand_more</ComponentIcon>
                      <ComponentIcon v-else>unfold_more</ComponentIcon>
                    </button>
                  </div>
                </th>

                <th class="px-0.5 py-2 text-left align-top font-normal">
                  <div>
                    <button
                      class="flex cursor-pointer items-center space-x-2 text-left"
                      :data-test="getTestId('data-section-sort-created-at-button')"
                      @click.prevent="orderBy('-created_at')"
                    >
                      <span>Eingesendet</span>
                      <ComponentIcon v-if="form.order === 'created_at'">expand_less</ComponentIcon>
                      <ComponentIcon v-else-if="form.order === '-created_at'">expand_more</ComponentIcon>
                      <ComponentIcon v-else>unfold_more</ComponentIcon>
                    </button>
                  </div>
                </th>

                <th class="px-0.5 py-2 text-left align-top font-normal">
                  <div>Kontaktperson</div>
                  <div class="text-xs">Telefonnummer, E-Mail</div>
                </th>

                <th />
              </tr>
            </thead>

            <tbody>
              <PatientinputClientComponentDataSectionTableRow
                v-for="transfer in transfers"
                :key="transfer.id"
                :transfer="transfer"
                :test-id="getTestId('data-section')"
                @start-import="startImport"
                @delete="startDelete"
              />
            </tbody>
          </table>
        </template>

        <!-- Paginator -->
        <ComponentPagination
          v-if="!form.processing && transfersEncrypted.meta"
          :links="transfersEncrypted.meta.links"
        />
      </article>
      <!-- table listing :: end -->
    </div>
  </section>

  <PatientinputClientComponentDeleteDialog
    ref="refDeleteDialog"
    :transfer="transferToDelete"
    :test-id="getTestId('data-tsection')"
    @cancel-delete="cancelDelete"
    @perform-delete="performDelete"
  />

  <PatientinputClientComponentImportDialog ref="refImportDialog" :test-id="getTestId('data-section')" />
</template>
